/* Сегментированный контрол с бегунком (.seg общего набора). Бегунок едет к новой вкладке, а не возникает на ней (APP-SPEC §1.4 п. 3);
   пересчитывается после раскладки, при прокрутке ряда и при смене размера окна — как в эталоне;
   догрузка шрифтов пересчёта не вызывает (bot-studio.js, homeLayout: на document.fonts.ready только fitTrend). */
import { useLayoutEffect, useRef, type ReactNode } from 'react';
import { segInd } from './seg-ind';
import { reduced } from './motion';

/** Растворение краёв ряда там, где есть продолжение (BotCore.fade): 26 px или 0 */
function segFade(el: HTMLElement) {
  const max = el.scrollWidth - el.clientWidth;
  el.style.setProperty('--ml', el.scrollLeft > 2 ? '26px' : '0px');
  el.style.setProperty('--mr', el.scrollLeft < max - 2 ? '26px' : '0px');
}
/** Выбранный пункт подводится в видимую часть ряда (BotCore.reveal): smooth — плавно (behavior BC.smooth(),
    после нажатия), иначе сразу. Без движения (.still, «уменьшить движение») — всегда сразу */
function segReveal(el: HTMLElement, smooth = false) {
  const active = el.querySelector<HTMLElement>('button.on');
  if (!active) return;
  const origin = el.getBoundingClientRect().left + el.clientLeft - el.scrollLeft;
  const left = active.getBoundingClientRect().left - origin - 12;
  const right = left + 24 + active.offsetWidth;
  const target = left < el.scrollLeft ? Math.max(0, left) : right > el.scrollLeft + el.clientWidth ? right - el.clientWidth : null;
  if (target == null) return;
  if (smooth && !reduced() && typeof el.scrollTo === 'function') el.scrollTo({ left: target, behavior: 'smooth' });
  else el.scrollLeft = target;
}

export interface SegItem<K extends string> {
  key: K;
  label: ReactNode;
  id?: string;
  controls?: string;
  disabled?: boolean;
}

interface Props<K extends string> {
  items: SegItem<K>[];
  value: K;
  onChange(k: K): void;
  id?: string;
  className?: string;
  label?: string;
  /** Атрибут кнопки для выбора: aria-selected (вкладки) или aria-checked (radiogroup) */
  aria?: 'aria-selected' | 'aria-checked' | 'aria-pressed';
  role?: 'tablist' | 'radiogroup' | 'group';
  /** Свой атрибут data-* для кнопки (совместимость разметки с прототипом) */
  dataKey?: string;
  /** Ряд без ролей и без растворения краёв: сегмент форматов главной (appsHTML эталона — голый
      div.seg.sm.anim.st-fseg с кнопками data-cat, ни ролей, ни aria, ни fadeWatch). */
  plain?: boolean;
  /** Выбор на месте (тот же ряд, другой пункт) подводит пункт плавно — segSync(id, true) эталона после нажатия;
      первый показ, пересборка ряда и смена размера — сразу, как segSync(id) */
  smooth?: boolean;
}

export function Seg<K extends string>({
  items,
  value,
  onChange,
  id,
  className,
  label,
  aria = 'aria-selected',
  role = 'tablist',
  dataKey,
  plain = false,
  smooth = false,
}: Props<K>) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const sync = () => {
      segInd(el);
      if (!plain) segFade(el);
    };
    const raf = requestAnimationFrame(sync);
    /* пересчёт — только там, где его делает эталон: после раскладки (rAF), при прокрутке ряда
       (BotCore.fadeWatch) и при смене размера окна с задержкой 120 мс (bot-studio.js, обработчик
       resize: layoutApps, fitTrend, segSync). Догрузка шрифтов пересчёта не вызывает: в эталоне на
       document.fonts.ready стоит только fitTrend, и бегунок остаётся по первому обмеру.
       Наблюдателя за размером в эталоне нет: там раскладку двигает движок явными вызовами segSync.
       У нас таких вызовов нет, поэтому ряд, смонтированный с нулевой шириной внутри позже
       раскрывающегося контейнера, сам себя не поправит, — для этого случая оставлен
       ResizeObserver. На догрузку шрифта он не срабатывает (проверено), так что ложных
       расхождений с эталоном не даёт. */
    let timer = 0;
    const resized = () => {
      clearTimeout(timer);
      /* по смене размера эталон делает segSync — бегунок, подвод выбранного и растворение (bot-studio.js: segSync) */
      timer = window.setTimeout(() => {
        segInd(el);
        segReveal(el);
        if (!plain) segFade(el);
      }, 120);
    };
    addEventListener('resize', resized);
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(resized);
    observer?.observe(el);
    const scrolled = () => segFade(el);
    if (!plain) el.addEventListener('scroll', scrolled, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(timer);
      removeEventListener('resize', resized);
      observer?.disconnect();
      if (!plain) el.removeEventListener('scroll', scrolled);
    };
  }, [plain]);
  const itemKey = items.map(item => `${item.key}:${!!item.disabled}`).join('|');
  const shown = useRef({ value, itemKey });
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const was = shown.current;
    shown.current = { value, itemKey };
    segInd(el);
    segReveal(el, smooth && was.value !== value && was.itemKey === itemKey);
    if (!plain) segFade(el);
  }, [value, itemKey, plain, smooth]);
  return (
    <div
      className={'seg' + (className ? ' ' + className : '')}
      id={id}
      role={plain ? undefined : role}
      aria-label={plain ? undefined : label}
      ref={ref}
      onKeyDown={event => {
        /* стрелками ходят только ряды с ролью: у голого ряда кнопки обычные, их обходит Tab */
        if (plain || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
        const enabled = items.filter(item => !item.disabled);
        if (!enabled.length) return;
        const buttons = [...(ref.current?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)') ?? [])];
        const index = Math.max(0, buttons.indexOf(document.activeElement as HTMLButtonElement));
        const next =
          event.key === 'Home'
            ? 0
            : event.key === 'End'
              ? enabled.length - 1
              : (index + (event.key === 'ArrowRight' ? 1 : -1) + enabled.length) % enabled.length;
        event.preventDefault();
        buttons[next]?.focus();
        onChange(enabled[next].key);
      }}
    >
      {items.map(it => {
        const on = it.key === value;
        const data = dataKey ? { [dataKey]: it.key } : {};
        return (
          <button
            key={it.key}
            type="button"
            role={plain ? undefined : role === 'tablist' ? 'tab' : role === 'radiogroup' ? 'radio' : undefined}
            disabled={it.disabled}
            tabIndex={plain || role === 'group' || on ? undefined : -1}
            id={it.id}
            aria-controls={it.controls}
            {...(plain ? {} : { [aria]: String(on) })}
            className={on ? 'on' : ''}
            {...data}
            onClick={() => on || onChange(it.key)}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}
